import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { capitalizeText } from '~/utils/misc'

interface HeaderSearchPlacesProps {
	countryCode?: string
	city?: string
	setCountry: (country: string) => void
	setCity: (city: string) => void
	countries: { countryCode: string; country: string }[] | undefined
	cities:
		| {
				city: string
				country: string
				countryCode: string
				count: number
		  }[]
		| undefined
}

export default function HeaderSearchPlaces({
	countryCode,
	city,
	setCountry,
	setCity,
	countries,
	cities,
}: HeaderSearchPlacesProps) {
	const { t } = useTranslation()
	const dropdownRef = useRef<HTMLDivElement | null>(null)
	const fetcher = useFetcher()

	const handleCountryChange = (countryCode: string) => {
		setCountry(countryCode)
		if (countryCode) {
			fetcher.load(`/api/get-cities?countryCode=${countryCode.toLowerCase()}`)
		}
	}

	return (
		<div
			className="relative z-10 flex w-full flex-col gap-4 bg-background md:flex-row"
			ref={dropdownRef}
		>
			<select
				className="flex-1 cursor-pointer rounded-full border border-border bg-card px-3 py-2 text-sm font-medium shadow-sm hover:border-foreground/50 focus:outline-none"
				onChange={e => {
					const countryCode = e.target.value.toLowerCase()
					handleCountryChange(countryCode)
				}}
				value={countryCode?.toLowerCase() || ''}
				aria-label={t('misc_country')}
			>
				<option value="">{t('public_everywhere')}</option>
				{countries?.map(country => (
					<option key={country.countryCode} value={country.countryCode}>
						{capitalizeText(country.country)}
					</option>
				))}
			</select>

			<select
				disabled={!countryCode}
				className="flex-1 cursor-pointer rounded-full border border-border bg-card px-3 py-2 text-sm font-medium shadow-sm hover:border-foreground/50 focus:outline-none"
				value={city?.toLowerCase() || ''}
				onChange={e => setCity(e.target.value)}
				aria-label={t('misc_city')}
			>
				<option value="">{t('misc_all_cities')}</option>
				{fetcher.data?.cities
					? fetcher.data.cities.map(city => (
							<option key={city.city} value={city.city}>
								{capitalizeText(city.city)}
							</option>
						))
					: cities?.map(city => (
							<option key={city.city} value={city.city}>
								{capitalizeText(city.city)}
							</option>
						))}
			</select>
		</div>
	)
}
